import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from '../api';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  FormControlLabel,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Card,
  CardMedia,
  Grid,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Delete,
  Save,
  PhotoCamera,
  Videocam,
  Description,
  Mic,
  Stop,
  PlayArrow,
  Pause,
  LocationOn,
  MyLocation,
  ArrowBack,
  Home,
  Add as AddIcon,
  Remove as RemoveIcon,
  Business,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Empresas fixas para seleção de funcionários
const EMPRESAS_FIXAS = [
  'Empresa Mercadão',
  'Terceirizada hidraulica',
  'Elétrica',
  'Incêndio',
  'Segurança',
];

export default function RelatorioObras() {
  const navigate = useNavigate();
  const handleBack = () => navigate('/');
  const { id: editId } = useParams(); // ID para modo de edição
  const { token } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const isEditMode = Boolean(editId);

  // Dados do relatório
  const [clienteId, setClienteId] = useState('');
  // Descrição de serviço
  const [descricaoServico, setDescricaoServico] = useState('');

  // Funcionários por empresa/setor
  const [funcionariosPorEmpresa, setFuncionariosPorEmpresa] = useState(
    EMPRESAS_FIXAS.map((nome) => ({ nome, funcionarios: 0, fixo: true }))
  );
  const [novaEmpresaNome, setNovaEmpresaNome] = useState('');
  const [novaEmpresaFuncionarios, setNovaEmpresaFuncionarios] = useState(0);

  // Localização (GPS)
  const [useLocation, setUseLocation] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);

  // Arquivos de mídia
  const [fotos, setFotos] = useState([]); // { file, preview } ou { url, isExisting }
  const [videos, setVideos] = useState([]); // { file, preview } ou { url, isExisting }
  const [documentos, setDocumentos] = useState([]); // { file, name } ou { url, name, isExisting }

  // Fotos/videos/docs existentes no servidor (para edição)
  const [existingFotos, setExistingFotos] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);
  const [existingFotosDescricoes, setExistingFotosDescricoes] = useState([]);
  const [existingVideosDescricoes, setExistingVideosDescricoes] = useState([]);
  const [existingDocsDescricoes, setExistingDocsDescricoes] = useState([]);
  const [existingAudioUrl, setExistingAudioUrl] = useState(null);

  // Gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcricao, setTranscricao] = useState('');
  const [transcrevendo, setTranscrevendo] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const recognitionRef = useRef(null);

  // Refs para inputs de arquivo - Fotos
  const fotoCameraInputRef = useRef(null); // Câmera frontal para foto
  const fotoGaleriaInputRef = useRef(null); // Galeria de fotos
  // Refs para inputs de arquivo - Vídeos
  const videoCameraInputRef = useRef(null); // Câmera traseira para vídeo
  const videoGaleriaInputRef = useRef(null); // Galeria de vídeos
  // Refs para inputs de arquivo - Documentos
  const docInputRef = useRef(null);

  // Carregar dados existentes para edição
  useEffect(() => {
    const fetchRelatorio = async () => {
      if (!editId || !token) return;

      setLoadingData(true);
      try {
        const res = await fetch(`${API_BASE}/relatorios_obras/${editId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          throw new Error('Erro ao carregar relatório');
        }

        const data = await res.json();

        // Preencher campos
        setClienteId(data.cliente_id || '');
        setDescricaoServico(data.descricao_servico || '');
        setTranscricao(data.transcricao_audio || '');

        // Carregar funcionários por empresa (mao_de_obra)
        if (data.mao_de_obra) {
          try {
            const maoDeObraData =
              typeof data.mao_de_obra === 'string'
                ? JSON.parse(data.mao_de_obra)
                : data.mao_de_obra;
            if (Array.isArray(maoDeObraData) && maoDeObraData.length > 0) {
              // Mesclar com empresas fixas
              const empresasCarregadas = maoDeObraData.map((e) => ({
                nome: e.nome,
                funcionarios: e.funcionarios || 0,
                fixo: EMPRESAS_FIXAS.includes(e.nome),
              }));
              // Adicionar empresas fixas que não estão nos dados
              EMPRESAS_FIXAS.forEach((nomeFixo) => {
                if (!empresasCarregadas.some((e) => e.nome === nomeFixo)) {
                  empresasCarregadas.push({ nome: nomeFixo, funcionarios: 0, fixo: true });
                }
              });
              setFuncionariosPorEmpresa(empresasCarregadas);
            }
          } catch (e) {
            console.warn('Erro ao parsear mao_de_obra:', e);
          }
        }

        if (data.latitude && data.longitude) {
          setLocation({
            latitude: data.latitude,
            longitude: data.longitude,
            accuracy: data.accuracy,
            capturedAt: data.location_captured_at || null,
          });
          setUseLocation(true);
        }

        // URLs de mídia existentes
        if (data.fotos_urls) setExistingFotos(data.fotos_urls);
        if (data.videos_urls) setExistingVideos(data.videos_urls);
        if (data.documentos_urls) setExistingDocs(data.documentos_urls);
        setExistingFotosDescricoes(Array.isArray(data.fotos_descricoes) ? data.fotos_descricoes : []);
        setExistingVideosDescricoes(Array.isArray(data.videos_descricoes) ? data.videos_descricoes : []);
        setExistingDocsDescricoes(Array.isArray(data.documentos_descricoes) ? data.documentos_descricoes : []);
        if (data.audio_url) setExistingAudioUrl(data.audio_url);
      } catch (e) {
        console.error('Erro ao carregar relatório:', e);
        setSnack({ open: true, message: 'Erro ao carregar relatório', severity: 'error' });
      } finally {
        setLoadingData(false);
      }
    };

    fetchRelatorio();
  }, [editId, token]);

  // Carregar clientes
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        // Verificar quais clientes o usuário tem acesso
        let clienteIds = null;
        try {
          const meResponse = await fetch(`${API_BASE}/me/clientes`, { headers: authHeaders });
          if (meResponse.ok) {
            const meData = await meResponse.json();
            clienteIds = meData.clientes || [];
          }
        } catch (e) {
          console.warn('Endpoint /me/clientes não disponível');
        }

        const res = await fetch(`${API_BASE}/clientes`, { headers: authHeaders });
        if (res.ok) {
          let data = await res.json();
          // Filtrar se houver restrição
          if (clienteIds && clienteIds.length > 0) {
            data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
          }
          setClientes(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Erro ao carregar clientes:', e);
      }
    };
    if (token) fetchClientes();
  }, [token]);

  // Handlers de upload de arquivos
  const handleFotoChange = (e) => {
    const files = Array.from(e.target.files);
    setFotos((prev) => {
      const remaining = 10 - (existingFotos.length + prev.length);
      if (remaining <= 0) {
        setSnack({ open: true, message: 'Limite de 10 fotos atingido.', severity: 'warning' });
        return prev;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        setSnack({
          open: true,
          message: `Apenas ${remaining} foto(s) adicionada(s). Limite de 10 atingido.`,
          severity: 'warning',
        });
      }
      return [
        ...prev,
        ...allowed.map((file) => ({ file, preview: URL.createObjectURL(file), descricao: '' })),
      ];
    });
    e.target.value = '';
  };

  const removerFoto = (index) => {
    setFotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    setVideos((prev) => {
      const remaining = 10 - (existingVideos.length + prev.length);
      if (remaining <= 0) {
        setSnack({ open: true, message: 'Limite de 10 vídeos atingido.', severity: 'warning' });
        return prev;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        setSnack({
          open: true,
          message: `Apenas ${remaining} vídeo(s) adicionado(s). Limite de 10 atingido.`,
          severity: 'warning',
        });
      }
      return [
        ...prev,
        ...allowed.map((file) => ({ file, preview: URL.createObjectURL(file), descricao: '' })),
      ];
    });
    e.target.value = '';
  };

  const removerVideo = (index) => {
    setVideos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDocChange = (e) => {
    const files = Array.from(e.target.files);
    setDocumentos((prev) => {
      const remaining = 10 - (existingDocs.length + prev.length);
      if (remaining <= 0) {
        setSnack({ open: true, message: 'Limite de 10 documentos atingido.', severity: 'warning' });
        return prev;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        setSnack({
          open: true,
          message: `Apenas ${remaining} documento(s) adicionado(s). Limite de 10 atingido.`,
          severity: 'warning',
        });
      }
      return [...prev, ...allowed.map((file) => ({ file, name: file.name, descricao: '' }))];
    });
    e.target.value = '';
  };

  const removerDocumento = (index) => {
    setDocumentos((prev) => prev.filter((_, i) => i !== index));
  };

  const capturarLocalizacao = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada neste dispositivo');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          capturedAt: new Date().toISOString(),
        });
        setUseLocation(true);
        setLocationLoading(false);
      },
      (err) => {
        setLocationError(err.message || 'Falha ao obter localização');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Solicitar permissão de localização ao entrar na página
  useEffect(() => {
    if (!navigator.geolocation) return;
    setUseLocation(true);
    capturarLocalizacao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleLocation = (checked) => {
    setUseLocation(checked);
    if (checked) {
      capturarLocalizacao();
    }
  };

  // Gravação de áudio com transcrição em tempo real
  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);

      // Iniciar transcrição em tempo real com Web Speech API
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'pt-BR';

        let finalTranscript = '';

        recognitionRef.current.onresult = (event) => {
          let interimTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }
          setTranscricao(finalTranscript + interimTranscript);
        };

        recognitionRef.current.onerror = (event) => {
          console.warn('Erro no reconhecimento de voz:', event.error);
        };

        recognitionRef.current.start();
        setTranscrevendo(true);
      }
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      setSnack({ open: true, message: 'Não foi possível acessar o microfone', severity: 'error' });
    }
  };

  const pararGravacao = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    // Parar transcrição
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setTranscrevendo(false);
    }
  };

  const playPauseAudio = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const removerAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscricao('');
    setIsPlaying(false);
    setTranscrevendo(false);
  };

  // Handlers de funcionários por empresa
  const handleFuncionarioChange = (index, valor) => {
    const novoValor = Math.max(0, parseInt(valor) || 0);
    setFuncionariosPorEmpresa((prev) => {
      const nova = [...prev];
      nova[index] = { ...nova[index], funcionarios: novoValor };
      return nova;
    });
  };

  const handleAdicionarEmpresa = () => {
    if (!novaEmpresaNome.trim()) return;
    const nomeNormalizado = novaEmpresaNome.trim();
    // Evitar duplicatas
    if (
      funcionariosPorEmpresa.some((e) => e.nome.toLowerCase() === nomeNormalizado.toLowerCase())
    ) {
      setSnack({ open: true, message: 'Empresa já existe', severity: 'warning' });
      return;
    }
    setFuncionariosPorEmpresa((prev) => [
      ...prev,
      {
        nome: nomeNormalizado,
        funcionarios: Math.max(0, parseInt(novaEmpresaFuncionarios) || 0),
        fixo: false,
      },
    ]);
    setNovaEmpresaNome('');
    setNovaEmpresaFuncionarios(0);
  };

  const handleRemoverEmpresa = (index) => {
    setFuncionariosPorEmpresa((prev) => prev.filter((_, i) => i !== index));
  };

  const getTotalFuncionarios = () => {
    return funcionariosPorEmpresa.reduce((acc, e) => acc + (e.funcionarios || 0), 0);
  };

  // Verificar se há algum dado preenchido para habilitar o botão salvar
  const temDadosPreenchidos = useMemo(() => {
    return (
      clienteId ||
      descricaoServico ||
      fotos.length > 0 ||
      videos.length > 0 ||
      documentos.length > 0 ||
      audioBlob ||
      transcricao ||
      existingFotos.length > 0 ||
      existingVideos.length > 0 ||
      existingDocs.length > 0 ||
      existingAudioUrl
    );
  }, [
    clienteId,
    descricaoServico,
    fotos,
    videos,
    documentos,
    audioBlob,
    transcricao,
    existingFotos,
    existingVideos,
    existingDocs,
    existingAudioUrl,
  ]);

  // Salvar relatório
  const salvarRelatorio = async () => {
    if (!clienteId) {
      setSnack({ open: true, message: 'Selecione um cliente', severity: 'warning' });
      return;
    }

    if (useLocation && !location) {
      setSnack({
        open: true,
        message: 'Ative e capture a localização antes de salvar',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      // Criar FormData para enviar arquivos junto com os dados
      const formData = new FormData();
      formData.append('cliente_id', clienteId);
      formData.append('descricao_servico', descricaoServico);
      formData.append('transcricao_audio', transcricao);

      // Funcionários por empresa (filtrar apenas os que têm funcionários > 0)
      const empresasComFuncionarios = funcionariosPorEmpresa.filter((e) => e.funcionarios > 0);
      formData.append('mao_de_obra', JSON.stringify(empresasComFuncionarios));

      if (useLocation && location?.latitude && location?.longitude) {
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
        if (location.accuracy) formData.append('accuracy', location.accuracy);
        if (location.capturedAt) formData.append('location_captured_at', location.capturedAt);
      }

      if (!isEditMode) {
        formData.append('data_relatorio', new Date().toISOString());
      }

      // Adicionar novas fotos (apenas files, não URLs existentes)
      fotos.forEach((foto) => {
        if (foto.file) {
          formData.append(`fotos`, foto.file);
        }
      });
      formData.append('fotos_descricoes', JSON.stringify(fotos.map((f) => (f.descricao || '').slice(0, 100))));

      // Adicionar novos vídeos
      videos.forEach((video) => {
        if (video.file) {
          formData.append(`videos`, video.file);
        }
      });
      formData.append('videos_descricoes', JSON.stringify(videos.map((v) => (v.descricao || '').slice(0, 100))));

      // Adicionar novos documentos
      documentos.forEach((doc) => {
        if (doc.file) {
          formData.append(`documentos`, doc.file);
        }
      });
      formData.append('documentos_descricoes', JSON.stringify(documentos.map((d) => (d.descricao || '').slice(0, 100))));

      // Em edição sem novos uploads, permite atualizar descrições das mídias existentes
      if (isEditMode && fotos.length === 0 && videos.length === 0 && documentos.length === 0) {
        formData.append(
          'fotos_descricoes',
          JSON.stringify((existingFotosDescricoes || []).map((d) => (d || '').slice(0, 100)))
        );
        formData.append(
          'videos_descricoes',
          JSON.stringify((existingVideosDescricoes || []).map((d) => (d || '').slice(0, 100)))
        );
        formData.append(
          'documentos_descricoes',
          JSON.stringify((existingDocsDescricoes || []).map((d) => (d || '').slice(0, 100)))
        );
      }

      // Adicionar áudio se existir (novo gravado)
      if (audioBlob) {
        formData.append('audio', audioBlob, 'audio_descricao.webm');
      }

      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const url = isEditMode
        ? `${API_BASE}/relatorios_obras/${editId}`
        : `${API_BASE}/relatorios_obras/`;

      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: formData,
      });

      if (!res.ok) {
        let msg = `Falha ao salvar (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
        } catch {}
        throw new Error(msg);
      }

      const data = await res.json();
      setSnack({
        open: true,
        message: isEditMode ? 'Relatório atualizado com sucesso!' : 'Relatório salvo com sucesso!',
        severity: 'success',
      });

      // Limpar URLs de preview
      fotos.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      videos.forEach((v) => v.preview && URL.revokeObjectURL(v.preview));
      if (audioUrl) URL.revokeObjectURL(audioUrl);

      // Redirecionar para a página de detalhes do relatório
      setTimeout(() => {
        navigate(`/relatorio-obras/${data.id}`);
      }, 1000);
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao salvar relatório', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton onClick={handleBack} size="small" aria-label="Voltar ao Dashboard">
          <ArrowBack />
        </IconButton>
        <Breadcrumbs separator="/" sx={{ ml: 1, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={handleBack}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </Link>
          <Typography color="text.primary">Relatório de Obras</Typography>
        </Breadcrumbs>
      </Box>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? 'Editar Relatório de Obras' : 'Relatório de Obras (Inspeção)'}
      </Typography>

      <Paper sx={{ p: 3, mt: 2 }}>
        {/* Seleção de Cliente */}
        <TextField
          select
          fullWidth
          label="Cliente"
          value={clienteId}
          onChange={(e) => setClienteId(e.target.value)}
          sx={{ mb: 3 }}
          required
        >
          <MenuItem value="">
            <em>Selecione um cliente</em>
          </MenuItem>
          {clientes.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.nome}
            </MenuItem>
          ))}
        </TextField>

        <Divider sx={{ my: 3 }} />

        {/* Funcionários por Empresa/Setor */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Business /> Funcionários por Empresa/Setor
            <Chip
              label={`Total: ${getTotalFuncionarios()}`}
              color="primary"
              size="small"
              sx={{ ml: 'auto' }}
            />
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {funcionariosPorEmpresa.map((empresa, index) => (
              <Grid item xs={12} sm={6} md={4} key={empresa.nome}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, minWidth: 0 }}
                    noWrap
                    title={empresa.nome}
                  >
                    {empresa.nome}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleFuncionarioChange(index, empresa.funcionarios - 1)}
                    disabled={empresa.funcionarios <= 0}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <TextField
                    type="number"
                    size="small"
                    value={empresa.funcionarios}
                    onChange={(e) => handleFuncionarioChange(index, e.target.value)}
                    inputProps={{ min: 0, style: { textAlign: 'center', width: 50 } }}
                    sx={{ width: 70 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleFuncionarioChange(index, empresa.funcionarios + 1)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  {!empresa.fixo && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoverEmpresa(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Adicionar nova empresa */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="body2" color="primary" gutterBottom>
              Adicionar Nova Empresa
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
              <TextField
                label="Nome da Empresa"
                size="small"
                value={novaEmpresaNome}
                onChange={(e) => setNovaEmpresaNome(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Nº Funcionários"
                type="number"
                size="small"
                value={novaEmpresaFuncionarios}
                onChange={(e) =>
                  setNovaEmpresaFuncionarios(Math.max(0, parseInt(e.target.value) || 0))
                }
                inputProps={{ min: 0 }}
                sx={{ width: 120 }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAdicionarEmpresa}
                disabled={!novaEmpresaNome.trim()}
              >
                Adicionar
              </Button>
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Descrição de Serviço */}
        <Typography variant="h6" color="primary" gutterBottom>
          Descrição de Serviço
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Descreva o serviço executado"
          value={descricaoServico}
          onChange={(e) => setDescricaoServico(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Localização */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <LocationOn /> Localização do Dispositivo
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={useLocation}
                onChange={(e) => handleToggleLocation(e.target.checked)}
                color="primary"
              />
            }
            label="Registrar localização antes de salvar"
          />
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              startIcon={<MyLocation />}
              onClick={capturarLocalizacao}
              disabled={locationLoading}
            >
              {locationLoading ? 'Capturando...' : 'Capturar localização'}
            </Button>
            {location && (
              <Chip
                label={`Lat ${location.latitude.toFixed(6)}, Lng ${location.longitude.toFixed(6)}`}
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          {useLocation && !location && !locationLoading && !locationError && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Para permitir a localização: toque no cadeado do navegador e permita “Localização”
              para este site. Depois clique em “Capturar localização”.
            </Alert>
          )}
          {locationError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {locationError}
              <br />
              Verifique a permissão do navegador para este site e tente novamente.
            </Alert>
          )}
          {useLocation && location && (
            <Box
              sx={{
                mt: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <iframe
                title="Mapa da localização"
                width="100%"
                height="220"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${location.longitude - 0.005}%2C${location.latitude - 0.005}%2C${location.longitude + 0.005}%2C${location.latitude + 0.005}&layer=mapnik&marker=${location.latitude}%2C${location.longitude}`}
              />
            </Box>
          )}
        </Box>

        {/* Gravação de Áudio */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Mic /> Gravação de Áudio com Transcrição Automática
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            {!isRecording ? (
              <Button
                variant="contained"
                color="error"
                startIcon={<Mic />}
                onClick={iniciarGravacao}
                disabled={audioBlob !== null}
              >
                Gravar Áudio
              </Button>
            ) : (
              <Button
                variant="contained"
                color="warning"
                startIcon={<Stop />}
                onClick={pararGravacao}
              >
                Parar Gravação
              </Button>
            )}
            {isRecording && (
              <>
                <Chip label="Gravando..." color="error" variant="outlined" />
                {transcrevendo && (
                  <Chip
                    icon={<CircularProgress size={14} />}
                    label="Transcrevendo..."
                    color="info"
                    variant="outlined"
                  />
                )}
              </>
            )}
          </Stack>

          {audioUrl && (
            <Box sx={{ mt: 2 }}>
              <audio
                ref={audioPlayerRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                style={{ display: 'none' }}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={playPauseAudio} color="primary">
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Áudio gravado
                </Typography>
                <IconButton onClick={removerAudio} color="error" size="small">
                  <Delete />
                </IconButton>
              </Stack>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Transcrição do áudio (edite se necessário)"
            value={transcricao}
            onChange={(e) => setTranscricao(e.target.value)}
            placeholder="A transcrição do áudio aparecerá aqui ou você pode digitar manualmente..."
            sx={{ mt: 2 }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Upload de Fotos */}
        <Typography variant="h6" color="primary" gutterBottom>
          Fotos ({existingFotos.length + fotos.length}/10)
        </Typography>
        <input
          type="file"
          accept="image/*"
          capture="user"
          multiple
          ref={fotoCameraInputRef}
          onChange={handleFotoChange}
          style={{ display: 'none' }}
        />
        <input
          type="file"
          accept="image/*"
          multiple
          ref={fotoGaleriaInputRef}
          onChange={handleFotoChange}
          style={{ display: 'none' }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PhotoCamera />}
            onClick={() => fotoCameraInputRef.current?.click()}
            disabled={existingFotos.length + fotos.length >= 10}
          >
            Tirar Foto
          </Button>
          <Button
            variant="outlined"
            onClick={() => fotoGaleriaInputRef.current?.click()}
            disabled={existingFotos.length + fotos.length >= 10}
          >
            Fototeca
          </Button>
        </Stack>
        {fotos.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {fotos.map((foto, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="120"
                    image={foto.preview}
                    alt={`Foto ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removerFoto(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                    }}
                  >
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </Card>
                <TextField
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  label="Descrição da foto"
                  value={foto.descricao || ''}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${(foto.descricao || '').length}/100`}
                  onChange={(e) =>
                    setFotos((prev) =>
                      prev.map((f, i) =>
                        i === index ? { ...f, descricao: e.target.value.slice(0, 100) } : f
                      )
                    )
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Upload de Vídeos */}
        <Typography variant="h6" color="primary" gutterBottom>
          Vídeos ({existingVideos.length + videos.length}/10)
        </Typography>
        <input
          type="file"
          accept="video/*"
          capture="environment"
          multiple
          ref={videoCameraInputRef}
          onChange={handleVideoChange}
          style={{ display: 'none' }}
        />
        <input
          type="file"
          accept="video/*"
          multiple
          ref={videoGaleriaInputRef}
          onChange={handleVideoChange}
          style={{ display: 'none' }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Videocam />}
            onClick={() => videoCameraInputRef.current?.click()}
            disabled={existingVideos.length + videos.length >= 10}
          >
            Gravar Vídeo
          </Button>
          <Button
            variant="outlined"
            onClick={() => videoGaleriaInputRef.current?.click()}
            disabled={existingVideos.length + videos.length >= 10}
          >
            Galeria de Vídeos
          </Button>
        </Stack>
        {videos.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {videos.map((video, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ position: 'relative' }}>
                  <video
                    src={video.preview}
                    style={{ width: '100%', height: 150, objectFit: 'cover' }}
                    controls
                  />
                  <IconButton
                    size="small"
                    onClick={() => removerVideo(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                    }}
                  >
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </Card>
                <TextField
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  label="Descrição do vídeo"
                  value={video.descricao || ''}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${(video.descricao || '').length}/100`}
                  onChange={(e) =>
                    setVideos((prev) =>
                      prev.map((v, i) =>
                        i === index ? { ...v, descricao: e.target.value.slice(0, 100) } : v
                      )
                    )
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Upload de Documentos */}
        <Typography variant="h6" color="primary" gutterBottom>
          Documentos ({existingDocs.length + documentos.length}/10)
        </Typography>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx"
          multiple
          ref={docInputRef}
          onChange={handleDocChange}
          style={{ display: 'none' }}
        />
        <Button
          variant="outlined"
          startIcon={<Description />}
          onClick={() => docInputRef.current?.click()}
          disabled={existingDocs.length + documentos.length >= 10}
          sx={{ mb: 2 }}
        >
          Adicionar Documentos
        </Button>
        {documentos.length > 0 && (
          <List dense sx={{ mb: 2 }}>
            {documentos.map((doc, index) => (
              <ListItem key={index} sx={{ display: 'block' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
                  <Description sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText primary={doc.name} />
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  label="Descrição do documento"
                  value={doc.descricao || ''}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${(doc.descricao || '').length}/100`}
                  onChange={(e) =>
                    setDocumentos((prev) =>
                      prev.map((d, i) =>
                        i === index ? { ...d, descricao: e.target.value.slice(0, 100) } : d
                      )
                    )
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => removerDocumento(index)} size="small">
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Mostrar mídia existente em modo de edição */}
        {isEditMode &&
          (existingFotos.length > 0 ||
            existingVideos.length > 0 ||
            existingDocs.length > 0 ||
            existingAudioUrl) && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" color="secondary" gutterBottom>
                Mídia Existente
              </Typography>

              {/* Fotos existentes */}
              {existingFotos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fotos Salvas ({existingFotos.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {existingFotos.map((url, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="120"
                            image={url}
                            alt={`Foto existente ${index + 1}`}
                            sx={{ objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => window.open(url, '_blank')}
                          />
                        </Card>
                        <TextField
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          label="Descrição da foto"
                          value={existingFotosDescricoes[index] || ''}
                          inputProps={{ maxLength: 100 }}
                          helperText={`${(existingFotosDescricoes[index] || '').length}/100`}
                          onChange={(e) =>
                            setExistingFotosDescricoes((prev) => {
                              const arr = [...prev];
                              arr[index] = e.target.value.slice(0, 100);
                              return arr;
                            })
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Vídeos existentes */}
              {existingVideos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vídeos Salvos ({existingVideos.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {existingVideos.map((url, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card>
                          <video
                            src={url}
                            style={{ width: '100%', height: 150, objectFit: 'cover' }}
                            controls
                          />
                        </Card>
                        <TextField
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          label="Descrição do vídeo"
                          value={existingVideosDescricoes[index] || ''}
                          inputProps={{ maxLength: 100 }}
                          helperText={`${(existingVideosDescricoes[index] || '').length}/100`}
                          onChange={(e) =>
                            setExistingVideosDescricoes((prev) => {
                              const arr = [...prev];
                              arr[index] = e.target.value.slice(0, 100);
                              return arr;
                            })
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Documentos existentes */}
              {existingDocs.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Documentos Salvos ({existingDocs.length})
                  </Typography>
                  <List dense>
                    {existingDocs.map((url, index) => (
                      <ListItem key={index} sx={{ display: 'block' }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                          onClick={() => window.open(url, '_blank')}
                        >
                          <Description sx={{ mr: 1, color: 'text.secondary' }} />
                          <ListItemText
                            primary={`Documento ${index + 1}`}
                            secondary="Clique para abrir"
                          />
                        </Box>
                        <TextField
                          size="small"
                          fullWidth
                          label="Descrição do documento"
                          value={existingDocsDescricoes[index] || ''}
                          inputProps={{ maxLength: 100 }}
                          helperText={`${(existingDocsDescricoes[index] || '').length}/100`}
                          onChange={(e) =>
                            setExistingDocsDescricoes((prev) => {
                              const arr = [...prev];
                              arr[index] = e.target.value.slice(0, 100);
                              return arr;
                            })
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Áudio existente */}
              {existingAudioUrl && !audioBlob && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Áudio Salvo
                  </Typography>
                  <audio controls src={existingAudioUrl} style={{ width: '100%' }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Grave um novo áudio acima para substituir este
                  </Typography>
                </Box>
              )}
            </>
          )}

        <Divider sx={{ my: 3 }} />

        {/* Botão Salvar */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => navigate('/relatorio-obras')}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={salvarRelatorio}
            disabled={loading || !temDadosPreenchidos}
            size="large"
          >
            {loading ? 'Salvando...' : isEditMode ? 'Atualizar Relatório' : 'Salvar Relatório'}
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
